import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { browserHistory } from 'react-router'
import StreamView from 'interview/components/sequenced-interview/stream-view'
import { mapLanguageToComponentProps } from 'interview/i18n'
import {
  startArchiving,
  stopArchiving,
  toggleRecordingState,
  disconnectSession,
} from 'actions/opentok'
import { updateState, saveResponse } from 'interview/actions/candidate'
import * as analytics from 'analytics/performance'
import {
  updateInterviewState,
  toNextQuestion,
  setCountdown,
  uploadSnapshot,
  clearCountdown,
  updateError,
} from 'interview/actions/sequenced-interview'
import config from 'config'
import { Body, H2, H3, Hr } from 'components/styled/typography'
import { Flex } from 'components/styled/containers/flex'
import styled from 'styled-components'
import QuestionView from '../question-view'
import getTextQuestionTime from '../get-text-question-time'
import {
  getCandidateInfoForAmplitude,
  getCompanyAndInterviewInfoForAmplitude,
} from 'selectors'

const propTypes = {
  i18n: PropTypes.shape({
    sequencedInterview: PropTypes.shape({
      questionAnswerView: PropTypes.shape({
        answerTimeInfo: PropTypes.func,
        title: PropTypes.func,
      }),
      browserInfo: PropTypes.shape({
        freezeInstruction: PropTypes.string,
      }),
    }),
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  jobOpening: PropTypes.shape({
    title: PropTypes.string.isRequired,
    questions: PropTypes.arrayOf(PropTypes.object).isRequired,
    additionalFormFields: PropTypes.arrayOf(PropTypes.object),
    formSetup: PropTypes.shape({}),
    company: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    }),
  }).isRequired,
  candidateState: PropTypes.string.isRequired,
  candidate: PropTypes.shape({
    _id: PropTypes.string,
    uuid: PropTypes.string.isRequired,
    videoResponses: PropTypes.arrayOf(
      PropTypes.shape({
        questionId: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  streaming: PropTypes.bool.isRequired,
  recording: PropTypes.bool.isRequired,
  publisher: PropTypes.shape({
    stream: PropTypes.shape({}),
    session: PropTypes.shape({}),
  }),
  session: PropTypes.shape({
    currentState: PropTypes.string,
  }),
  archive: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
  }),
  archiveFromEvent: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
  }),
  answerSaved: PropTypes.bool.isRequired,
  responseSaved: PropTypes.bool.isRequired,
  reconnecting: PropTypes.bool.isRequired,
  opentokError: PropTypes.string,
  candidateError: PropTypes.string,
  interviewState: PropTypes.shape({
    currentQuestion: PropTypes.shape({
      _id: PropTypes.string,
      videoUrl: PropTypes.string,
      title: PropTypes.string,
      textQuestion: PropTypes.string,
      takeLimit: PropTypes.number,
      answeringTime: PropTypes.number,
      preparationTime: PropTypes.number,
      answerable: PropTypes.bool,
    }),
    onAnswer: PropTypes.bool.isRequired,
    index: PropTypes.number.isRequired,
    toNextCountdown: PropTypes.bool.isRequired,
    answerCountdown: PropTypes.bool.isRequired,
    timerCount: PropTypes.number.isRequired,
    takeCount: PropTypes.number.isRequired,
    questionView: PropTypes.bool,
    loading: PropTypes.bool,
    stopped: PropTypes.bool,
  }).isRequired,
  errorType: PropTypes.string,
  connectionCount: PropTypes.number.isRequired,
}

const defaultProps = {
  publisher: undefined,
  opentokError: '',
  candidateError: '',
  session: undefined,
  archive: undefined,
  archiveFromEvent: undefined,
  errorType: '',
}

const mapStateToProps = state => ({
  archive: state.global.opentok.archive,
  archiveFromEvent: state.global.opentok.archiveFromEvent,
  session: state.global.opentok.session,
  responseSaved: state.global.opentok.responseSaved,
  publisher: state.global.opentok.publisher,
  streaming: state.global.opentok.streaming,
  recording: state.global.opentok.recording,
  opentokError: state.global.opentok.error,
  candidateError: state.interview.candidate.error,
  candidate: state.candidate,
  jobOpening: state.interview.jobOpening,
  candidateState: state.interview.candidate.state,
  answerSaved: state.interview.candidate.answerSaved,
  reconnecting: state.global.opentok.reconnecting,
  onAnswer: state.interview.sequencedInterview.interviewState.onAnswer,
  interviewState: state.interview.sequencedInterview.interviewState,
  errorType: state.interview.sequencedInterview.errorType,
  connectionCount: state.global.opentok.connectionCount,
})

const automaticArchiving =
  !config.DISABLE_SEQUENCED_INTERVIEW_AUTOMATIC_ARCHIVING

const InterviewContainer = styled(Flex)`
  padding: 0 ${({ theme }) => theme.space.s80};

  ${({ theme }) => theme.mediaQuery.tablet} {
    padding: 0 0;
  }
`

const HeaderContainer = styled(Flex)`
  width: 100%;
`

const VideoContainer = styled.div`
  width: 100%;
`
export class QuestionAnswerView extends Component {
  constructor(props) {
    super(props)

    this.state = {
      videoState: '',
    }

    this.onSubmit = this.onSubmit.bind(this)
    this.onRetake = this.onRetake.bind(this)
    this.onStop = this.onStop.bind(this)
    this.componentGracefulUnmount = this.componentGracefulUnmount.bind(this)
  }

  componentDidMount() {
    const {
      candidateState,
      jobOpening,
      candidate: { uuid, videoResponses },
      dispatch,
    } = this.props

    window.addEventListener('beforeunload', this.componentGracefulUnmount)

    if (candidateState.includes('question')) {
      const questionNumber = parseInt(candidateState.substring(9, 11), 10)
      const currentQuestion = jobOpening.questions[questionNumber - 1]
      const hasAnswer = videoResponses.find(
        r => r.questionId === currentQuestion._id && r.saved
      )
      const onAnswerState =
        candidateState.includes('answer') || candidateState.includes('finished')
      const takesLength =
        currentQuestion &&
        videoResponses.filter(r => r.questionId === currentQuestion._id).length
      const takes = takesLength || 1
      const takeLimit = currentQuestion.takeLimit || 1
      const questionView =
        candidateState.includes('videoquestion') ||
        candidateState.includes('textquestion')
      let stopped =
        candidateState.includes('finished') ||
        (candidateState.includes('videoquestion') && !hasAnswer) ||
        takesLength === takeLimit

      // render the correct view and time based on candidate state
      if (hasAnswer) {
        this.toNextQuestion(questionNumber, true)
      } else {
        if (
          (candidateState.includes('answer') && takesLength === takeLimit) ||
          (!onAnswerState && takesLength > 0)
        ) {
          // update state to finished if takes are gone
          // or if on question or prep state state and there are draft takes
          stopped = true
          const questionId = currentQuestion && currentQuestion._id
          dispatch(updateState(uuid, questionId))
        }
        dispatch(
          updateInterviewState({
            stopped,
            currentQuestion,
            questionView,
            index: questionNumber - 1,
            answerCountdown: !stopped && onAnswerState,
            toNextCountdown: false,
            onAnswer: onAnswerState,
            takeCount: stopped ? takes : takesLength + 1,
          })
        )
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      candidateState,
      candidate: { uuid, videoResponses },
      interviewState: { currentQuestion, index, answerCountdown },
      dispatch,
      streaming,
      publisher,
      reconnecting,
      archive,
      archiveFromEvent,
      session,
      opentokError,
      candidateError,
      answerSaved,
      errorType,
      recording,
      connectionCount,
    } = this.props
    const { videoState } = this.state

    const archiveChanged =
      archive &&
      (typeof prevProps.archive === 'undefined' ||
        archive.status !== prevProps.archive.status)
    const sessionDisconnected =
      session && session.currentState === 'disconnected'
    const questionText =
      currentQuestion && (currentQuestion.title || currentQuestion.textQuestion)
    const pausedOrStartedArchiveFromEvent =
      archiveFromEvent && archiveFromEvent.status !== 'stopped'
    const isStreaming = streaming && publisher && !sessionDisconnected
    const hasError =
      (opentokError !== prevProps.opentokError && opentokError.length > 0) ||
      (candidateError !== prevProps.candidateError &&
        candidateError.length > 0) ||
      (errorType !== prevProps.errorType && errorType.length > 0)
    const publisherStream = publisher && publisher.stream
    const tooManyConnections =
      session &&
      publisher &&
      publisher.session &&
      publisher.stream &&
      connectionCount > 1

    const archiveStopped =
      archiveFromEvent &&
      archive &&
      archiveFromEvent.id === archive.id &&
      archiveFromEvent.status === 'stopped'

    if (hasError) {
      this.handleError(archiveStopped)
    }

    if (tooManyConnections) {
      // disconnect session if more than one connection
      dispatch(disconnectSession())
      dispatch(updateError('tooManyConnections'))
    }

    if (streaming !== prevProps.streaming && !streaming && !publisherStream) {
      // clear countdown when streaming stops
      dispatch(clearCountdown())
    }

    if (
      videoState !== prevState.videoState &&
      videoState === 'pause' &&
      candidateState.includes('videoquestion')
    ) {
      const currentNotAnswerable =
        currentQuestion && !currentQuestion.answerable
      if (currentNotAnswerable) {
        this.toNextQuestion(index + 1, true)
      } else {
        dispatch(
          updateInterviewState({
            onAnswer: false,
            stopped: false,
            questionView: false,
          })
        )
        dispatch(updateState(uuid, currentQuestion._id))
      }
    }

    if (candidateState !== prevProps.candidateState) {
      this.handleCandidateStateChange()

      if (
        currentQuestion !== prevProps.interviewState.currentQuestion &&
        currentQuestion
      ) {
        const hasAnswer = videoResponses.find(
          r => r.questionId === currentQuestion._id && r.saved
        )
        // should do this only if candidate state and current queestion has changed so no duplicate calls
        if (hasAnswer) {
          this.toNextQuestion(index + 1, true)
        }
      }
    }
    // if session is disconnected ie. due to network connection is lost, stop the timer
    // if on answer and recording during this stop archiving without saving the response
    // and start the recording again (with 10 sec countdown in the beginning)
    if (reconnecting !== prevProps.reconnecting) {
      this.handleReconnectingChange(archiveStopped)
    }

    if (
      !streaming &&
      !publisherStream &&
      archiveFromEvent !== prevProps.archiveFromEvent &&
      !archive &&
      pausedOrStartedArchiveFromEvent
    ) {
      this.onStop(false, true)
    }

    if (
      streaming !== prevProps.streaming &&
      streaming &&
      publisher &&
      publisher.stream
    ) {
      if (
        (!archiveFromEvent || archiveFromEvent.status === 'stopped') &&
        answerCountdown &&
        !recording
      ) {
        dispatch(setCountdown(true))
      }
      // have to update stateTimestamp but not state here
      // because streaming and publishing is only now started
      dispatch(updateState(uuid, currentQuestion._id, true))
    }

    if (isStreaming && !hasError && !reconnecting) {
      if (answerSaved !== prevProps.answerSaved && answerSaved) {
        this.toNextQuestion(index + 1)
      }

      if (currentQuestion) {
        if (archiveChanged) {
          this.handleArchiveUpdate()
        }
        if (questionText) {
          this.handleInterval()
        }
      }
    }
  }

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer)
    }

    this.componentGracefulUnmount()
  }

  onStop(save = false, stopArchiveFromEvent = false) {
    const {
      dispatch,
      candidate: { uuid },
      archive,
      archiveFromEvent,
      interviewState: { currentQuestion },
    } = this.props

    const archiveToStop = stopArchiveFromEvent ? archiveFromEvent : archive
    const archiveId =
      archiveToStop && archiveToStop.id && archiveToStop.status !== 'stopped'
        ? archiveToStop.id
        : undefined
    const payload = { uuid, questionId: currentQuestion._id, archiveId, save }

    if (this.timer) {
      clearInterval(this.timer)
    }

    dispatch(toggleRecordingState(false))

    if (save) {
      dispatch(updateInterviewState({ loading: true, timerCount: 0 }))
    }

    dispatch(stopArchiving(payload))
  }

  onSubmit() {
    const {
      dispatch,
      candidate: { uuid },
      interviewState: { currentQuestion },
    } = this.props

    if (!automaticArchiving) {
      dispatch(toggleRecordingState(false))
    }

    const payload = {
      uuid,
      questionId: currentQuestion._id,
    }

    dispatch(updateInterviewState({ loading: true }))
    dispatch(saveResponse(payload))
  }

  onRetake() {
    const {
      dispatch,
      candidate: { uuid, _id },
      jobOpening: { company },
      interviewState: { takeCount },
    } = this.props

    const companyData = company && {
      companyId: company._id,
      companyName: company.name,
    }

    analytics.onClickEvent({
      locationId: 'timedInterviewPage',
      id: 'confirmRetake',
      properties: {
        candidateId: _id,
        ...companyData,
      },
    })

    dispatch(updateInterviewState({ takeCount: takeCount + 1 }))
    dispatch(updateState(uuid))
    dispatch(setCountdown(true))
  }

  getTotalTime() {
    const {
      interviewState: {
        onAnswer,
        currentQuestion,
        answerCountdown,
        toNextCountdown,
      },
    } = this.props

    if (answerCountdown || toNextCountdown) {
      return 10
    }

    if (onAnswer) {
      return currentQuestion.answeringTime
    }
    return currentQuestion.preparationTime
  }

  getQuestionTextClassName() {
    const {
      interviewState: { currentQuestion },
    } = this.props
    if (
      currentQuestion.textQuestion &&
      currentQuestion.textQuestion.length > 300
    ) {
      return 'length-300'
    }

    if (
      currentQuestion.textQuestion &&
      currentQuestion.textQuestion.length > 200
    ) {
      return 'length-200'
    }
    return ''
  }

  handleError(archiveStopped) {
    const { dispatch, archive, reconnecting, connectionCount, candidateError } =
      this.props

    // stop timer in case of error
    if (this.timer) {
      clearInterval(this.timer)
    }

    dispatch(toggleRecordingState(false))
    dispatch(clearCountdown())
    if (
      !reconnecting &&
      archive &&
      archive.status !== 'stopped' &&
      connectionCount === 1
    ) {
      if (!archiveStopped) {
        this.onStop()
      }
    }

    if (candidateError && candidateError === 'notFound') {
      browserHistory.push('/sequenced-interview/closed')
    }

    dispatch(updateInterviewState({ loading: false }))
  }

  handleCandidateStateChange() {
    const {
      candidateState,
      dispatch,
      interviewState: { stopped },
    } = this.props
    // stop timers when candidate moves to question view and question is a video question
    // and when recording stops and candidate moves to finished state
    if (
      candidateState.includes('videoquestion') ||
      candidateState.includes('finished')
    ) {
      if (this.timer) {
        clearInterval(this.timer)
      }
      dispatch(
        updateInterviewState({
          stopped: true,
          timerCount: 0,
          answerCountdown: false,
          onAnswer: candidateState.includes('finished'),
          questionView: candidateState.includes('videoquestion'),
        })
      )
    }

    // start timers when moving to question view and the question is a text question
    if (candidateState.includes('textquestion') && stopped) {
      dispatch(updateInterviewState({ stopped: false, timerCount: 0 }))
    }
  }

  handleReconnectingChange(archiveStopped) {
    const {
      archive,
      dispatch,
      reconnecting,
      candidateState,
      jobOpening,
      answerSaved,
      interviewState: { currentQuestion, index, toNextCountdown },
    } = this.props

    if (reconnecting) {
      if (this.timer) {
        clearInterval(this.timer)
      }

      dispatch(clearCountdown())

      dispatch(
        updateInterviewState({
          timerCount: 0,
          loading: false,
          answerCountdown: candidateState.includes('answer'),
          onAnswer:
            candidateState.includes('answer') ||
            candidateState.includes('finished'),
        })
      )
    } else if (archive && archive.status !== 'stopped') {
      if (!archiveStopped) {
        this.onStop()
      }
    } else if (candidateState.includes('answer')) {
      dispatch(setCountdown(true))
    } else if (
      candidateState.includes('finished') &&
      toNextCountdown &&
      answerSaved &&
      currentQuestion &&
      jobOpening.questions[index] &&
      currentQuestion._id === jobOpening.questions[index]._id
    ) {
      this.toNextQuestion(index + 1)
    }
  }

  handleInterval() {
    const {
      interviewState: {
        index,
        currentQuestion,
        timerCount,
        onAnswer,
        stopped,
        loading,
        answerCountdown,
      },
      dispatch,
      archive,
      recording,
      candidate: { uuid },
      candidateState,
      opentokError,
      candidateError,
      errorType,
    } = this.props

    if (this.timer) {
      clearInterval(this.timer)
    }

    const hasError = opentokError || candidateError || errorType
    const textQuestionLength =
      currentQuestion.textQuestion && currentQuestion.textQuestion.length
    const textQuestionTime = getTextQuestionTime(textQuestionLength)

    if (!stopped && !loading && !hasError) {
      this.timer = setInterval(() => {
        if (
          candidateState.includes('textquestion') &&
          timerCount === textQuestionTime
        ) {
          const currentNotAnswerable =
            currentQuestion && !currentQuestion.answerable
          if (currentNotAnswerable) {
            this.toNextQuestion(index + 1, true)
          } else {
            dispatch(updateState(uuid))
            dispatch(
              updateInterviewState({ timerCount: 0, questionView: false })
            )
          }
        } else if (
          !onAnswer &&
          candidateState.includes('preparation') &&
          timerCount === currentQuestion.preparationTime
        ) {
          dispatch(
            updateInterviewState({
              onAnswer: true,
              timerCount: 0,
              loading: true,
            })
          )
          dispatch(startArchiving({ uuid }))

          if (!automaticArchiving) {
            dispatch(toggleRecordingState(true))
          }
        }
        if (
          currentQuestion &&
          onAnswer &&
          candidateState.includes('answer') &&
          timerCount === currentQuestion.answeringTime &&
          !answerCountdown
        ) {
          if (recording && archive && archive.status !== 'stopped') {
            this.onStop(true)
          }
        }
        dispatch(
          updateInterviewState({
            timerCount: this.props.interviewState.timerCount + 1,
          })
        )
      }, 1000)
    }
  }

  handleArchiveUpdate() {
    const {
      dispatch,
      candidate: { uuid },
      candidateState,
      recording,
      archive,
      responseSaved,
    } = this.props

    if (recording && archive.status === 'started') {
      dispatch(
        updateInterviewState({ onAnswer: true, timerCount: 0, loading: false })
      )
      if (candidateState.includes('preparation')) {
        dispatch(updateState(uuid))
      }
      dispatch(uploadSnapshot())
      if (!automaticArchiving) {
        dispatch(toggleRecordingState(true))
      }
    } else if (archive.status === 'stopped') {
      dispatch(updateInterviewState({ loading: false }))
      // update state only when archive has stopped and response for the question has been saved
      if (responseSaved) {
        dispatch(updateState(uuid))
      } else if (candidateState.includes('answer')) {
        dispatch(setCountdown(true))
      }
      if (!automaticArchiving) {
        dispatch(toggleRecordingState(false))
      }
    }
  }

  componentGracefulUnmount() {
    const { dispatch, session } = this.props
    if (this.timer) {
      clearInterval(this.timer)
    }

    if (session && session.currentState === 'connected') {
      dispatch(disconnectSession())
    }

    window.removeEventListener('beforeunload', this.componentGracefulUnmount)
  }

  toNextQuestion(questionNumber, shouldMoveToNextQuestion) {
    const { jobOpening, dispatch } = this.props
    const nextQuestion = jobOpening.questions[questionNumber]
    dispatch(updateInterviewState({ loading: false }))
    if (!nextQuestion) {
      if (this.timer) {
        clearInterval(this.timer)
      }
    }
    dispatch(toNextQuestion(questionNumber, shouldMoveToNextQuestion))
  }

  updateVideoState(newState) {
    const {
      interviewState: { currentQuestion },
    } = this.props
    if (currentQuestion.videoUrl) {
      this.setState({ videoState: newState })
    }
  }

  render() {
    const {
      i18n,
      dispatch,
      candidate,
      jobOpening,
      candidateState,
      interviewState: {
        index,
        loading,
        onAnswer,
        timerCount,
        currentQuestion,
        questionView,
        stopped,
      },
      reconnecting,
      opentokError,
      candidateError,
      errorType,
      streaming,
      publisher,
    } = this.props
    const error = !!(opentokError || candidateError || errorType)
    const { uuid } = candidate
    const { questions } = jobOpening
    const candidateInfo = getCandidateInfoForAmplitude(candidate)
    const companyAndInterviewInfo =
      getCompanyAndInterviewInfoForAmplitude(jobOpening)

    const buttonEventData = {
      ...candidateInfo,
      ...companyAndInterviewInfo,
    }

    if (currentQuestion && candidateState.includes('question')) {
      const pastNonAnswerableQuestions = questions
        .slice(0, index)
        .filter(q => !q.answerable)
      const onQuestion =
        questionView ||
        candidateState.includes('videoquestion') ||
        candidateState.includes('textquestion')
      const nextQuestion = questions[index + 1]
      const questionText = onQuestion
        ? ''
        : currentQuestion.title || currentQuestion.textQuestion
      const answerableQuestions = questions.filter(q => q.answerable)
      const currentQuestionNumber =
        index + 1 - pastNonAnswerableQuestions.length
      const className = this.getQuestionTextClassName()
      const languageKeys = i18n.sequencedInterview.questionAnswerView
      const answerTime =
        currentQuestion &&
        currentQuestion.answerable &&
        currentQuestion.answeringTime

      const answerMinutes = Math.floor(answerTime / 60)
      const answerSeconds = answerTime - answerMinutes * 60
      const answerTimeInfo = languageKeys.answerTimeInfo(
        answerMinutes,
        answerSeconds
      )

      return (
        <InterviewContainer
          flexDirection="column"
          justifyContent="center"
          alignItems="flex-start"
        >
          <HeaderContainer mt="s16" mb="s16" justifyContent="space-between">
            <H2 color="bodyText">{i18n.interview.title}</H2>
            {currentQuestion && currentQuestion.answerable && (
              <H3 className={`question-text ${className}`}>
                {`${currentQuestionNumber}/${answerableQuestions.length}`}
              </H3>
            )}
          </HeaderContainer>
          <Body>{questionText}</Body>
          <VideoContainer>
            {onQuestion && (
              <QuestionView
                hasError={error}
                language={jobOpening.language}
                reconnecting={reconnecting}
                currentQuestion={currentQuestion}
                candidateState={candidateState}
                timerCount={timerCount}
                buttonText={
                  i18n.sequencedInterview.questionAnswerView.skipTextQuestion
                }
                updateTextQuestion={textQuestionTime =>
                  dispatch(
                    updateInterviewState({ timerCount: textQuestionTime })
                  )
                }
                updateState={newState => this.updateVideoState(newState)}
                languageKeys={languageKeys}
                buttonEventData={buttonEventData}
              />
            )}
            <StreamView
              questionView={onQuestion}
              stopped={stopped}
              reconnecting={reconnecting}
              hasError={error}
              onAnswer={onAnswer}
              candidateUuid={uuid}
              onStop={value => this.onStop(value)}
              onRetake={this.onRetake}
              onSubmit={this.onSubmit}
              submitting={loading}
              timerCount={timerCount}
              answerTimeInfo={answerTimeInfo}
              currentQuestion={currentQuestion}
              totalTime={this.getTotalTime()}
              nextQuestion={nextQuestion}
              streaming={streaming}
              publisher={publisher}
            />
            <Hr />
            <Body>{i18n.sequencedInterview.browserInfo.freezeInstruction}</Body>
          </VideoContainer>
        </InterviewContainer>
      )
    }
    return null
  }
}

QuestionAnswerView.propTypes = propTypes
QuestionAnswerView.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(
  QuestionAnswerView
)
